<template>
  <div v-show="!displayHistory" :class="'flex flex-col ' + size">
    <div v-if="!viewOnly" class="flex flex-row justify-evenly flex-wrap text-sm -mb-5">
      <div class="flex flex-row gap-6 justify-evenly my-2 h-10">
        <div class="flex flex-row gap-2">
          <ToolbarButtons
            label="Budget"
            small
            show-import
            show-create
            show-add
            :add-disabled="drawItems.length == 0"
            add-tooltip="Add Line Items"
            @import="importBudgetClick"
            @create="createBudgetClick"
            @add="addItemsClick"
          ></ToolbarButtons>
        </div>
        <TemplateDropdown
          v-model:selected-template="budget"
          :templates="budgets"
          class="h-8!"
          option-label="budgetName"
          show-load
          show-save
          show-delete
          @load="loadBudgetClick"
          @save="saveBudgetClick"
          @delete="deleteBudgetClick"
        ></TemplateDropdown>
      </div>
      <div class="flex flex-row my-2 justify-evenly gap-6 h-8">
        <InputGroup class="w-fit!">
          <Select
            v-model="specialLineItem"
            :options="specialLineItems"
            optionLabel="description"
            showClear
            placeholder="Select a special line item"
            class="w-72 rounded-r-none align-middle"
            pt:label:class="self-center"
          ></Select>
          <InputGroupAddon class="py-2">
            <small class="mr-1">Add To Last Row</small>
            <Checkbox v-model="insertAtEnd" :binary="true" size="small"></Checkbox>
          </InputGroupAddon>
          <IconButton v-tooltip.top="'Insert'" icon="plus" outlined small @click="addSpecialLineItem"></IconButton>
          <IconButton v-tooltip.top="'Edit'" icon="cog" outlined small @click="displaySpecialLineItemEditor = true"></IconButton>
        </InputGroup>
        <ToggleButton
          v-if="drawNumber == 0"
          v-model="projectSettings.enablePercentageBudget"
          on-label="Budget Tool On"
          off-label="Budget Tool Off"
          class="text-sm"
          @change="budgetToolChanged"
        ></ToggleButton>
        <ToggleButton
          v-model="projectSettings.linearNumbering"
          on-label="Auto Number On"
          off-label="Auto Number Off"
          class="text-sm"
          @change="lineNumberingChanged"
        ></ToggleButton>
        <ToggleButton
          v-model="projectSettings.autoDisburseFunds"
          on-label="Auto Disburse On"
          off-label="Auto Disburse Off"
          class="text-sm"
          :disabled="projectSettings.automaticDefaultPayments"
          @change="recalculate"
        ></ToggleButton>
        <IconButton v-tooltip.top="'Change Columns'" icon="cog" outlined small @click="customizeBudgetColumns = true"></IconButton>
        <IconButton v-if="drawId != null" v-tooltip.top="'Retainage'" icon="percentage" outlined small @click="showRetainage"></IconButton>
      </div>
    </div>
    <div :class="addProject ? 'h-fit' : ''" style="width: 100%; flex: 1 1 auto">
      <ag-grid-vue
        theme="legacy"
        gridId="budgetBuilderGrid"
        :immutableData="true"
        :getRowId="(params: GetRowIdParams) => (params.rowPinned ? params.data.description : params.data.drawItemId)"
        :rowData="drawItems"
        :rowClassRules="rowClassRules"
        :columnDefs="colDefs"
        :defaultColDef="defaultColDefs"
        :autoSizeStrategy="{ type: 'fitCellContents' }"
        :stopEditingWhenCellsLoseFocus="true"
        :suppressDragLeaveHidesColumns="true"
        :pinnedBottomRowData="footers"
        :getRowHeight="getRowHeight"
        :animateRows="false"
        enter-navigates-vertically-after-edit
        single-click-edit
        rowSelection="single"
        class="pt-3 h-full ag-font-small"
        :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
        @grid-size-changed="resize"
        @cell-editing-stopped="cellEditComplete"
        @grid-ready="(event: GridReadyEvent) => (gridApi = event.api)"
      ></ag-grid-vue>
    </div>
  </div>
  <div v-show="displayHistory" :class="'flex flex-col ' + size" class="h-full">
    <BudgetHistory :draw-item-history :budget-columns="projectSettings.budgetColumns"></BudgetHistory>
    <div class="flex justify-between mt-3">
      <BackButton @click="closeDrawItemHistory"></BackButton>
    </div>
  </div>
  <TSDialog v-model:visible="displayImportModal" label="Import Budget" icon="file-import" size="w-[1000px]!">
    <template #content>
      <BudgetFileImportModal @import-complete="importBudget"></BudgetFileImportModal>
    </template>
  </TSDialog>
  <TSDialog
    v-model:visible="displayModal"
    :label="modalMode === 'CREATE' ? 'Create Budget' : 'Add Line Item'"
    size="w-auto!"
    :icon="modalMode === 'CREATE' ? 'wrench' : 'plus-circle'"
  >
    <template #content>
      <BudgetModal :mode="modalMode" @create-items="createItems"></BudgetModal>
    </template>
  </TSDialog>
  <TSDialog v-model:visible="displaySaveModal" label="Save Budget" size="w-[600px]!" icon="save">
    <template #content>
      <div class="flex flex-col gap-2 my-2">
        <label for="budgetName" class="font-bold my-auto">Name:</label>
        <InputText name="budgetName" type="text" class="h-10" v-model="budgetName" autofocus v-on:keyup.enter="saveBudgetConfirm" />
        <SubmitButton label="Save Budget" @click="saveBudgetConfirm"></SubmitButton>
      </div>
    </template>
  </TSDialog>
  <TSDialog label="Customize Budget Columns" v-model:visible="customizeBudgetColumns" v-if="customizeBudgetColumns" icon="cog" size="w-[900px]!">
    <template #content>
      <CustomizableColumnManager
        modal
        :initial-rows="projectSettings.budgetColumns"
        :scope="SettingScope.BUDGET_COLUMN_TEMPLATES"
        @confirm="budgetColumnsCustomized"
      ></CustomizableColumnManager>
    </template>
  </TSDialog>
  <TSDialog label="Category Load Tool" v-model:visible="displayCategoryTool" v-if="displayCategoryTool" icon="wrench" size="w-[750px]!">
    <template #content>
      <CategoryLoadTool :cust-cost-types @submitted="loadCategories" @cancelled="displayCategoryTool = false"></CategoryLoadTool>
    </template>
  </TSDialog>
  <TSDialog
    label="Special Line Item Manager"
    v-model:visible="displaySpecialLineItemEditor"
    v-if="displaySpecialLineItemEditor"
    icon="cog"
    size="w-[750px]!"
  >
    <template #content>
      <CustomBudgetItemManagerPanel @items-updated="(items: StockBudgetItem[]) => (specialLineItems = items)"></CustomBudgetItemManagerPanel>
    </template>
  </TSDialog>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import CustomDeleteIconCellRenderer from "@/components/cell-renderers/CustomDeleteIconCellRenderer.vue";
import CustomInfoIconCellRenderer from "../cell-renderers/CustomInfoIconCellRenderer.vue";
import CutChecksRenderer from "@/components/cell-renderers/CutChecksRenderer.vue";
import { createFooterRow, createNewDrawItem, createNewRetainage, recalculateRows, showColumns, updateTotals } from "@/services/BudgetBuilderService";
import { usePropsStore } from "@/stores/props";
import { DrawItemHistoryListSchema, type DrawItem, type DrawItemHistory } from "@/types/DrawItem";
import type { Code } from "@/types/Codes";
import {
  getCodeTable,
  numberParser,
  percentParser,
  textParser,
  getCellEditableClass,
  percentFormatter,
  currencyFormatter,
  testBit,
  round,
} from "@/services/Utility";
import { CodeTableNumber } from "@/enums/CodeTable";
import { BudgetItemListSchema, BudgetListSchema, type Budget, type BudgetItem } from "@/types/Budget";
import { useConfirm } from "primevue/useconfirm";
import { type StockBudgetItem, StockBudgetItemListSchema } from "@/types/StockBudgetItem";
import type { ProjectInfo, ProjectSettings } from "@/types/Projects";
import type { ProjectEntity } from "@/types/ProjectEntity";
import { recalculateSourceOfFunds } from "@/services/SourceOfFundsService";
import type { UserPermissions } from "@/types/User";
import { permissionError } from "@/services/Permissions";
import type { ProjectReminder } from "@/types/Reminder";
import { checkBudgetBasedReminders } from "@/services/ReminderService";
import { BudgetColumns } from "@/enums/BudgetColumns";
import {
  type ColDef,
  type CellClassParams,
  type EditableCallbackParams,
  type RowClassParams,
  type RowHeightParams,
  type ValueFormatterParams,
  type ValueParserParams,
  type GridApi,
  type GridReadyEvent,
  type IRowNode,
  type GetRowIdParams,
  type CellEditingStoppedEvent,
} from "ag-grid-community";
import HeaderPasteButton from "../cell-renderers/HeaderPasteButton.vue";
import type { EntityRetainage, Retainage } from "@/types/Retainage";
import { v4 as uuidv4 } from "uuid";
import HeaderToolButton from "../cell-renderers/HeaderToolButton.vue";
import type { CategoryLoad } from "./CategoryLoadTool.vue";
import type { CustomizableColumn } from "@/types/CustomizableColumns";
import { SettingScope } from "@/enums/SystemSettings";

const confirm = useConfirm();

const projectSettings = defineModel<ProjectSettings>("projectSettings", { required: true });
const drawItems = defineModel<DrawItem[]>("drawItems", { required: true });
const projectInfo = defineModel<ProjectInfo>("projectInfo", { required: true });
const projectEntities = defineModel<ProjectEntity[]>("projectEntities", { required: true });
const projectReminders = defineModel<ProjectReminder[]>("projectReminders", { required: true });
const entityRetainageList = defineModel<EntityRetainage[]>("entityRetainageList", { required: false });

interface Props {
  drawNumber?: number;
  size?: string;
  permissions: UserPermissions;
  drawLocked?: boolean;
  drawId?: string;
  viewOnly?: boolean;
  entityRetainageList?: EntityRetainage[];
  addProject?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  drawNumber: 0,
  size: "h-[74vh]",
  drawLocked: false,
  viewOnly: false,
  addProject: false,
});

const emit = defineEmits<{
  historyOpened: [];
  historyClosed: [];
  showRetainage: [];
}>();

const custCostTypes = ref<Code[]>([]);
const custCostTypesDisplay = ref<string[]>([]);

const drawItemHistory = ref<DrawItemHistory[]>();
const displayHistory = ref<boolean>(false);
const drawItemHistoryMap: { [rootItemId: string]: DrawItemHistory[] } = {};

const budget = ref<Budget>();
const budgets = ref<Budget[]>([]);
const budgetName = ref<string>("");

const specialLineItem = ref<StockBudgetItem | null>(null);
const specialLineItems = ref<StockBudgetItem[]>([]);
const insertAtEnd = ref<boolean>(true);

const displayModal = ref(false);
const displaySaveModal = ref(false);
const displayImportModal = ref(false);
const customizeBudgetColumns = ref(false);
const displayCategoryTool = ref(false);
const displaySpecialLineItemEditor = ref(false);
const modalMode = ref<"CREATE" | "ADD">("CREATE");

const gridApi = ref<GridApi>();
const propsStore = usePropsStore();
const darkVal = computed<boolean>(() => propsStore.darkMode);

const rearrangeColumns = () => {
  const order: string[] = [];
  projectSettings.value.budgetColumnOrder.forEach((col) => {
    const entry = Object.entries(BudgetColumns).find((entry) => entry[1].bit === col);
    if (entry == null) return;
    order.push(entry[0]);
  });
  colDefs.value = colDefs.value.sort((a, b) => order.indexOf(a.field ?? "") - order.indexOf(b.field ?? ""));
  gridApi.value?.setGridOption("columnDefs", colDefs.value);
};

const recalculateTotals = (): void => {
  updateTotals(footers, drawItems, gridApi);
};

const getBudgetColumnOrder = (): number[] => {
  if (gridApi.value == null) return [];
  return gridApi.value.getColumnState().map((col: any) => {
    let fieldKey = col.colId as keyof typeof BudgetColumns;
    return BudgetColumns[fieldKey].bit;
  });
};

defineExpose({ gridApi, rearrangeColumns, recalculateTotals, getBudgetColumnOrder });

const footers = ref([createFooterRow("TOTAL")]);

const rowClassRules = ref({
  "font-bold": (params: RowClassParams) => {
    return params.node.rowPinned != null;
  },
  "text-red-500!": (params: RowClassParams) => {
    return params.data.fundsAvailable < 0;
  },
});

// Just disable this for now, couldn't find a good way to get it working when retainage is added and removed
// watch(
//   () => projectInfo.value.hasRetainage,
//   (hasRetainage: boolean) =>
//     gridApi.value?.setColumnsVisible(
//       ["totalRetainagePriorDraw", "totalReleasedPriorDraw", "totalRetainage", "totalReleased", "retainageBalance"],
//       hasRetainage
//     )
// );

const getBudgets = (): void => {
  axios
    .get("/budgets")
    .then((response) => {
      parseData(BudgetListSchema, response.data.result, budgets);
    })
    .catch((e) => errorApi(e));
};

const getSpecialLineItems = (): void => {
  axios
    .get("/stock-budget-items")
    .then((response) => {
      parseData(StockBudgetItemListSchema, response.data.result, specialLineItems);
    })
    .catch((e) => errorApi(e));
};

const getRowHeight = (params: RowHeightParams): number => {
  return params.node.rowPinned ? 18 : 26;
};

const cellClass = (params: CellClassParams): string => {
  const centered = ["lineRefNumber", "custCostType", "costType", "cutChecks"];
  const modifier = centered.includes(params.colDef.field ?? "") ? " text-center" : "";
  if (
    ((params.colDef.editable && params.column.isCellEditable(params.node)) || params.colDef.field === "cutChecks") &&
    !props.viewOnly &&
    params.data.custCostType != null
  )
    return getCellEditableClass() + modifier;
  return "" + modifier;
};

const defaultColDefs = {
  floatingFilter: false,
  sortable: false,
  enableCellChangeFlash: false,
  wrapHeaderText: true,
  autoHeaderHeight: true,
  cellClass: (params: CellClassParams) => cellClass(params),
  width: 150,
};

const resize = () => {
  gridApi.value?.sizeColumnsToFit();
};

const lineNumberingChanged = () => {
  adjustLineRefsAndOrdinals();

  gridApi.value?.refreshCells({
    force: true,
    columns: ["lineRefNumber"], // colId of the column you want to refresh
  });
};

const budgetToolChanged = () => {
  gridApi.value?.setColumnsVisible(["percentBudget"], projectSettings.value.enablePercentageBudget);
};

const adjustLineRefsAndOrdinals = () => {
  if (!projectSettings.value.linearNumbering) return;

  let index = 1;
  drawItems.value = drawItems.value.map((item) => {
    let newItem: DrawItem;
    if (item.parentDrawItemId == "" || item.parentDrawItemId == "-1") {
      if (!projectSettings.value.linearNumbering) {
        newItem = { ...item, ordinal: index };
      } else {
        newItem = { ...item, lineRefNumber: index.toString(), ordinal: index };
      }
    } else {
      newItem = item;
    }
    index += 1;
    return newItem;
  });
  nextTick(() => drawItems.value.forEach((item) => updateEntityRetainage(item)));
};

const addItemsClick = () => {
  if (!props.permissions.editProject) {
    permissionError();
    return;
  }
  modalMode.value = "ADD";
  displayModal.value = true;
};

const addSpecialLineItem = () => {
  if (specialLineItem.value == null) return;

  if (
    specialLineItem.value.description === "DRAW BASED RETAINAGE" &&
    drawItems.value.filter((item: DrawItem) => item.description === "DRAW BASED RETAINAGE").length > 0
  ) {
    error("Invalid Item", "Only one draw based retainage item can exist on the budget");
    return;
  }

  const item: DrawItem = createNewDrawItem(props.drawId);
  item.costType = specialLineItem.value.costType;
  item.custCostType = specialLineItem.value.custCostType;
  item.description = specialLineItem.value.description;

  let insertIndex = 0;
  const selectedNodes = gridApi.value?.getSelectedNodes();
  if (insertAtEnd.value || selectedNodes == null || selectedNodes.length === 0) {
    insertIndex = drawItems.value.length;
  } else {
    insertIndex = selectedNodes[0].sourceRowIndex;
  }

  item.ordinal = insertIndex + 1;
  drawItems.value = [...drawItems.value.slice(0, insertIndex), item, ...drawItems.value.slice(insertIndex)];
  addRetainage(insertIndex, [item]);

  nextTick(() => {
    adjustLineRefsAndOrdinals();
    updateTotals(footers, drawItems, gridApi);
  });
};

const createItems = (rowsToAdd: number, modalOption: boolean) => {
  displayModal.value = false;

  let startIndex = 0;

  if (modalMode.value == "CREATE") {
    projectSettings.value.linearNumbering = modalOption;
  } else {
    const selectedNodes = gridApi.value?.getSelectedNodes();
    if (modalOption || selectedNodes == null || selectedNodes.length === 0) {
      startIndex = drawItems.value.length;
    } else {
      startIndex = selectedNodes[0].sourceRowIndex;
    }
  }

  const items: DrawItem[] = [];
  for (let i = startIndex; i < startIndex + rowsToAdd; i++) {
    const item = createNewDrawItem(props.drawId);
    item.ordinal = i + 1;
    item.lineRefNumber = projectSettings.value.linearNumbering ? item.ordinal.toString() : "";
    items.push(item);
  }

  if (modalMode.value == "CREATE") {
    drawItems.value.forEach((item) => deleteRetainage(item.drawItemId));
    drawItems.value = items;
    addRetainage(0, items);
  } else {
    drawItems.value = [...drawItems.value.slice(0, startIndex), ...items, ...drawItems.value.slice(startIndex)];
    addRetainage(startIndex, items);
  }

  nextTick(() => {
    adjustLineRefsAndOrdinals();
    updateTotals(footers, drawItems, gridApi);
  });
};

const deleteBudgetItem = (source: DrawItem) => {
  deleteRetainage(source.drawItemId);

  drawItems.value = drawItems.value.filter((item) => item.drawItemId !== source.drawItemId);

  nextTick(() => {
    adjustLineRefsAndOrdinals();
    updateTotals(footers, drawItems, gridApi);
  });
  success("Budget Item Deleted", "An item was successfully deleted from the budget");
};

const addRetainage = (startIndex: number, items: DrawItem[]): void => {
  if (entityRetainageList.value == null) return;
  entityRetainageList.value.forEach((er) => {
    const retainageList: Retainage[] = [];
    items.forEach((item) => retainageList.push(createNewRetainage(item, er.projectEntityId)));
    er.retainageList = [...er.retainageList.slice(0, startIndex), ...retainageList, ...er.retainageList.slice(startIndex)];
  });
};

const deleteRetainage = (drawItemId: string): void => {
  if (entityRetainageList.value == null) return;
  entityRetainageList.value.forEach((er) => {
    const index = er.retainageList.findIndex((r) => r.drawItemId == drawItemId);
    const removed = er.retainageList.splice(index, 1);
    er.deletedRetainageList.push(...removed);
  });
};

const loadBudget = async () => {
  if (budget.value == null) return;

  projectSettings.value.linearNumbering = budget.value.linearNumbering;
  projectSettings.value.autoDisburseFunds = !budget.value.standardCostType;
  projectSettings.value.budgetColumns = budget.value.columns;
  projectSettings.value.budgetColumnOrder = budget.value.columnOrder;

  rearrangeColumns();
  showColumns(projectInfo.value, projectSettings.value.budgetColumns, gridApi);

  await axios
    .get(`/budgets/${budget.value.budgetId}/items`)
    .then((response) => {
      const budgetItems = BudgetItemListSchema.parse(response.data.result);
      if (budgetItems != null) {
        const items: DrawItem[] = [];
        budgetItems.forEach((item: BudgetItem) => {
          const newItem = createNewDrawItem(props.drawId);
          newItem.ordinal = item.ordinal;
          newItem.lineRefNumber = item.lineRef;
          newItem.description = item.description;
          newItem.costType = item.costType;
          newItem.custCostType = item.custCostType;
          newItem.budgetAmount = item.budgetAmount;
          newItem.percentBudget = item.percentBudget;
          newItem.flags = item.flags;

          items.push(newItem);
        });

        drawItems.value.forEach((item) => deleteRetainage(item.drawItemId));
        drawItems.value = items;
        addRetainage(0, items);

        resize();
      }
    })
    .catch((e) => errorApi(e));

  success("Budget Loaded", "");
  nextTick(() => {
    updateTotals(footers, drawItems, gridApi);
  });
};

const importBudgetClick = () => {
  if (drawItems.value.length > 0) {
    confirmPopup(
      confirm,
      "Import Budget",
      "The current budget will be replaced by the imported budget. Do you wish to continue?",
      () => (displayImportModal.value = true),
      () => info("Import Cancelled", "")
    );
  } else {
    displayImportModal.value = true;
  }
};

const importBudget = async (importedItems: DrawItem[]) => {
  for (let i = 0; i < importedItems.length; i++) {
    if (importedItems[i].lineRefNumber !== i.toString()) {
      projectSettings.value.linearNumbering = false;
      lineNumberingChanged();
      break;
    }
  }

  await recalculateRows(importedItems, projectSettings.value).then((recalcItems: DrawItem[]) => {
    drawItems.value.forEach((item) => deleteRetainage(item.drawItemId));
    recalcItems.forEach((item) => {
      item.drawItemId = uuidv4();
      item.newItem = true;
    });
    drawItems.value = recalcItems;
    addRetainage(0, recalcItems);
    displayImportModal.value = false;
    nextTick(() => {
      updateTotals(footers, drawItems, gridApi);
    });
  });
  recalculateSourceOfFunds(projectEntities.value, drawItems.value, entityRetainageList.value, projectSettings.value, props.drawNumber === 0).then(
    (recalcEntities: ProjectEntity[]) => (projectEntities.value = recalcEntities)
  );
  checkReminders();
};

const createBudgetClick = () => {
  modalMode.value = "CREATE";
  displayModal.value = true;
};

const loadBudgetClick = () => {
  if (drawItems.value.length > 0) {
    confirmPopup(confirm, "Load Budget", "Are you sure you wish to load the selected budget?", loadBudget, () => info("Load Cancelled", ""));
  } else {
    loadBudget();
  }
};

const saveBudgetClick = async () => {
  await recalculateRows(drawItems.value, projectSettings.value).then((recalcItems: DrawItem[]) => {
    drawItems.value = recalcItems;
  });

  await recalculateSourceOfFunds(
    projectEntities.value,
    drawItems.value,
    entityRetainageList.value,
    projectSettings.value,
    props.drawNumber === 0
  ).then((recalcEntities: ProjectEntity[]) => (projectEntities.value = recalcEntities));
  budgetName.value = budget.value ? budget.value.budgetName : "";
  displaySaveModal.value = true;
};

const saveBudget = () => {
  const newBudget: Budget = {
    budgetId: "",
    budgetName: budgetName.value,
    linearNumbering: projectSettings.value.linearNumbering,
    standardCostType: !projectSettings.value.autoDisburseFunds,
    columns: projectSettings.value.budgetColumns,
    columnOrder: getBudgetColumnOrder(),
  };

  const newBudgetItems: BudgetItem[] = [];
  drawItems.value.forEach((item: DrawItem) => {
    const newItem: BudgetItem = {
      budgetItemId: "",
      budgetId: "",
      ordinal: item.ordinal,
      lineRef: item.lineRefNumber,
      description: item.description,
      costType: item.costType,
      custCostType: item.custCostType,
      budgetAmount: item.budgetAmount,
      percentBudget: item.percentBudget,
      flags: item.flags,
    };
    newBudgetItems.push(newItem);
  });

  axios
    .post("/budgets/save", { budget: newBudget, items: newBudgetItems })
    .then((response) => {
      handleApiResponse(
        response,
        (result: string) => {
          getBudgets();
          budget.value = undefined;
          success("Budget Saved", "");
          displaySaveModal.value = false;
        },
        (errors: ApiError[]) => {
          error("Cannot save budget", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const saveBudgetConfirm = () => {
  if (budgetName.value.trim() === "") {
    error("Invalid Name", "The budget name may not be blank");
    return;
  }

  const budgetToOverride = budgets.value.find((b: Budget) => b.budgetName.toUpperCase() === budgetName.value.toUpperCase());
  if (budgetToOverride != null) {
    confirmPopup(
      confirm,
      "Replace Budget",
      "A budget with this name already exists.\nDo you wish to replace it?",
      () => {
        deleteBudget(false);
        saveBudget();
      },
      () => info("Replace Cancelled", "")
    );
  } else {
    saveBudget();
  }
};

const budgetColumnsCustomized = (columns: number): void => {
  projectSettings.value.budgetColumns = columns;
  showColumns(projectInfo.value, projectSettings.value.budgetColumns, gridApi);
  customizeBudgetColumns.value = false;
};

const loadCategories = async (loadData: CategoryLoad[]): Promise<void> => {
  loadData.forEach((data: CategoryLoad) => {
    data.lineItems.forEach((lineIndex: number) => {
      drawItems.value[lineIndex - 1].custCostType = data.category.name;
      if (data.costType != null) drawItems.value[lineIndex - 1].costType = data.costType;
    });
  });
  displayCategoryTool.value = false;

  await recalculateRows(drawItems.value, projectSettings.value).then((recalcItems: DrawItem[]) => {
    drawItems.value = recalcItems;
  });
  await recalculateSourceOfFunds(
    projectEntities.value,
    drawItems.value,
    entityRetainageList.value,
    projectSettings.value,
    props.drawNumber === 0
  ).then((recalcEntities: ProjectEntity[]) => (projectEntities.value = recalcEntities));
};

const showRetainage = (): void => {
  emit("showRetainage");
};

const recalculate = async () => {
  if (drawItems.value.length === 0) return;
  {
    await recalculateRows(drawItems.value, projectSettings.value).then((recalcItems: DrawItem[]) => {
      drawItems.value = recalcItems;
    });
    await recalculateSourceOfFunds(
      projectEntities.value,
      drawItems.value,
      entityRetainageList.value,
      projectSettings.value,
      props.drawNumber === 0
    ).then((recalcEntities: ProjectEntity[]) => (projectEntities.value = recalcEntities));

    checkReminders();
  }
};

const deleteBudget = (showSuccessMessage: boolean) => {
  if (budget.value == null) return;

  axios
    .post(`/budgets/delete/${budget.value.budgetId}`)
    .then((response) => {
      handleApiResponse(
        response,
        (result: string) => {
          getBudgets();
          budget.value = undefined;
          if (showSuccessMessage) success("Budget Deleted", "");
        },
        (errors: ApiError[]) => {
          error("Cannot delete value", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const deleteBudgetClick = () => {
  confirmPopup(
    confirm,
    "Delete Budget",
    "Are you sure you want to delete the selected budget template?",
    () => deleteBudget(true),
    () => info("Delete Cancelled", "")
  );
};

const setCutChecks = (id: string, checked: boolean) => {
  drawItems.value = drawItems.value.map((item) => (id === item.drawItemId ? { ...item, cutChecks: checked } : item));
};

const showDrawItemHistory = (item: DrawItem) => {
  emit("historyOpened");
  drawItemHistory.value = undefined;
  displayHistory.value = true;

  if (item.rootItemId.length == 0) {
    drawItemHistory.value = [{ drawNumber: props.drawNumber, item: item }];
  } else {
    const items = drawItemHistoryMap[item.rootItemId];

    if (items == null) {
      axios
        .get(`/draw-items/history/${item.rootItemId}`)
        .then((response) => {
          handleApiResponse(
            response,
            (result: string) => {
              drawItemHistory.value = DrawItemHistoryListSchema.parse(result);
              drawItemHistoryMap[item.rootItemId] = DrawItemHistoryListSchema.parse(result);
            },
            (errors: ApiError[]) => {
              error("Error Occurred", errors[0].message);
            }
          );
        })
        .catch((e) => errorApi(e));
    } else {
      drawItemHistory.value = items;
    }
  }
};

const closeDrawItemHistory = () => {
  emit("historyClosed");
  drawItemHistory.value = undefined;
  displayHistory.value = false;
};

const isDrawBasedRetainage = (params: EditableCallbackParams): boolean => {
  return params.data.description === "DRAW BASED RETAINAGE";
};

const hideColumn = (column: CustomizableColumn): boolean => {
  return projectSettings.value.budgetColumns == null ? false : !testBit(projectSettings.value.budgetColumns, column.bit);
};

const paste = async (column: string, pasteValue: string, type: string, rounding: number, editable: any) => {
  if (pasteValue == null) {
    warn("Could Not Find Values To Paste", "");
    return;
  }

  pasteValue = pasteValue.trim();

  let values = pasteValue.split("\n");

  let parsedValues: any[] = [];
  if (type === "number") {
    for (let value of values) {
      if (value == null || value.trim() == "") {
        parsedValues.push(null);
        continue;
      }

      value = value.trim();
      if (value === "-") value = "0";
      let newValue = Number(value.trim().replace(/[^0-9\.-]+/g, ""));
      if (Number.isNaN(newValue)) {
        error("Unable to parse value: " + value, "");
        return;
      }

      parsedValues.push(round(newValue, rounding));
    }
  } else if (type == "text") {
    parsedValues = values;
  }

  let col = gridApi.value?.getColumn(column);
  if (col == null) return;

  let index = 0;
  drawItems.value = drawItems.value.map((item) => {
    const row = gridApi.value?.getDisplayedRowAtIndex(index);
    let val = parsedValues[index];
    const newItem: DrawItem =
      index < parsedValues.length && row != null && col.isCellEditable(row) && val != null ? { ...item, [column]: val } : item;
    index += 1;
    return newItem;
  });

  if (column === "description" || column === "lineRefNumber") return;

  nextTick(() => {
    pasteRefresh();
  });
};

const showCategoryTool = (): void => {
  displayCategoryTool.value = true;
};

const pasteRefresh = async (): Promise<void> => {
  await recalculateRows(drawItems.value, projectSettings.value).then((recalcItems: DrawItem[]) => {
    drawItems.value = recalcItems;
  });
  await recalculateSourceOfFunds(
    projectEntities.value,
    drawItems.value,
    entityRetainageList.value,
    projectSettings.value,
    props.drawNumber === 0
  ).then((recalcEntities: ProjectEntity[]) => (projectEntities.value = recalcEntities));

  checkReminders();
  updateTotals(footers, drawItems, gridApi);

  success("Values Pasted", "");
};

const columnEditable = (params: EditableCallbackParams | IRowNode): boolean => {
  return params.data.custCostType != null && !props.viewOnly;
};

const colDefs = ref<ColDef[]>([
  {
    field: "delete",
    headerName: "",
    cellClass: "text-center flex items-center justify-center",
    cellRenderer: CustomDeleteIconCellRenderer,
    cellRendererParams: {
      deleteConfirmMessage: (data: DrawItem) => `Are you sure you want to delete item "${data.description}" from the budget?`,
      deleteConfirmHeader: () => "Delete Budget Item",
      callback: deleteBudgetItem,
      checkDeletable: (data: DrawItem) => data.custCostType != null,
      size: "small",
    },
    width: 40,
    minWidth: 40,
    pinned: true,
    hide: props.viewOnly,
  },
  {
    field: "history",
    headerName: "",
    cellClass: "text-center flex items-center justify-center",
    cellRenderer: CustomInfoIconCellRenderer,
    cellRendererParams: {
      callback: showDrawItemHistory,
      checkInfo: (data: DrawItem) => data.custCostType != null,
      icon: "pi-history",
      size: "small",
    },
    width: 45,
    minWidth: 45,
    pinned: true,
  },
  {
    field: "lineRefNumber",
    cellDataType: "text",
    headerName: "Line #",
    headerClass: "grid-align-center",
    editable: (params: EditableCallbackParams) => columnEditable(params) && !projectSettings.value.linearNumbering,
    valueParser: (params: ValueParserParams) => textParser(params),
    pinned: true,
    width: 75,
    minWidth: 75,
  },
  {
    field: "description",
    cellDataType: "text",
    headerName: "Description",
    headerClass: "grid-align-center",
    headerComponent: HeaderPasteButton,
    headerComponentParams: (params: any) => {
      return {
        paste: paste,
        type: "text",
        show: !props.viewOnly,
      };
    },
    editable: (params: EditableCallbackParams) => columnEditable(params) && !isDrawBasedRetainage(params),
    valueParser: (params: ValueParserParams) => textParser(params),
    width: 200,
    minWidth: 200,
    pinned: true,
  },
  {
    field: "custCostType",
    cellDataType: "text",
    headerName: "Category",
    headerClass: "grid-align-center",
    headerComponent: HeaderToolButton,
    headerComponentParams: (params: any) => {
      return {
        clicked: showCategoryTool,
        show: !props.viewOnly,
      };
    },
    width: 100,
    minWidth: 100,
    cellEditor: "agSelectCellEditor",
    cellEditorParams: {
      values: [""],
    },
    editable: (params: EditableCallbackParams) => columnEditable(params),
  },
  {
    field: "costType",
    cellDataType: "text",
    headerName: "Cost Type",
    headerClass: "grid-align-center",
    width: 100,
    minWidth: 100,
    cellEditor: "agSelectCellEditor",
    cellEditorParams: {
      values: ["INSP", "NONINSP", "SUB"],
    },
    editable: (params: EditableCallbackParams) => columnEditable(params) && !isDrawBasedRetainage(params),
  },
  {
    field: "initialBudget",
    cellDataType: "number",
    headerName: "Initial Budget",
    headerClass: "text-center",
    valueFormatter: currencyFormatter,
    initialHide: hideColumn(BudgetColumns.initialBudget),
    width: 125,
    minWidth: 125,
  },
  {
    field: "budgetAmount",
    cellDataType: "number",
    headerName: "Budget Amount",
    headerComponent: HeaderPasteButton,
    headerComponentParams: (params: any) => {
      return {
        paste: paste,
        type: "number",
        rounding: 2,
        show: !props.viewOnly && props.drawNumber === 0 && !props.drawLocked,
      };
    },
    valueFormatter: currencyFormatter,
    editable: (params: EditableCallbackParams) =>
      columnEditable(params) && props.drawNumber === 0 && !props.drawLocked && !isDrawBasedRetainage(params),
    valueParser: (params: ValueParserParams) => numberParser(params),
    initialHide: hideColumn(BudgetColumns.budgetAmount),
    width: 125,
    minWidth: 125,
  },
  {
    field: "budgetAdjustment",
    cellDataType: "number",
    headerName: "Budget Adjustment",
    headerComponent: HeaderPasteButton,
    headerComponentParams: {
      paste: paste,
      type: "number",
      rounding: 2,
      show: !props.viewOnly,
    },
    valueFormatter: currencyFormatter,
    editable: (params: EditableCallbackParams) => columnEditable(params) && !props.drawLocked && !isDrawBasedRetainage(params),
    valueParser: (params: ValueParserParams) => numberParser(params, 2, true),
    width: 125,
    minWidth: 125,
  },
  {
    field: "cumulativeAdjustment",
    cellDataType: "number",
    headerName: "Cumulative Adjustment",
    headerClass: "text-center",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.cumulativeAdjustment),
    width: 125,
    minWidth: 125,
  },
  {
    field: "adjustedBudget",
    cellDataType: "number",
    headerName: "Adjusted Budget",
    valueFormatter: currencyFormatter,
    width: 125,
    minWidth: 125,
  },
  {
    field: "priorDrawnToDate",
    cellDataType: "number",
    headerName: "Prior Drawn To Date",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.priorDrawnToDate),
    width: 125,
    minWidth: 125,
  },
  {
    field: "totalThisDraw",
    cellDataType: "number",
    headerName: "This Draw Loan",
    headerComponent: HeaderPasteButton,
    headerComponentParams: {
      paste: paste,
      type: "number",
      rounding: 2,
      show: !props.viewOnly,
    },
    valueFormatter: currencyFormatter,
    editable: (params: EditableCallbackParams) =>
      columnEditable(params) && !props.drawLocked && !isDrawBasedRetainage(params) && params.data.custCostType !== "EQUITY",
    valueParser: (params: ValueParserParams) => numberParser(params, 2, true),
    initialHide: hideColumn(BudgetColumns.totalThisDraw),
    width: 125,
    minWidth: 125,
  },
  {
    field: "totalThisEquity",
    cellDataType: "number",
    headerName: "This Draw Equity",
    headerComponent: HeaderPasteButton,
    headerComponentParams: {
      paste: paste,
      type: "number",
      rounding: 2,
      show: !props.viewOnly,
    },
    valueFormatter: currencyFormatter,
    editable: (params: EditableCallbackParams) => columnEditable(params) && !props.drawLocked && !isDrawBasedRetainage(params),
    valueParser: (params: ValueParserParams) => numberParser(params, 2, true),
    initialHide: hideColumn(BudgetColumns.totalThisEquity),
    width: 125,
    minWidth: 125,
  },
  {
    field: "netThisDraw",
    cellDataType: "number",
    headerName: "Net This Draw Loan",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.netThisDraw),
    width: 125,
    minWidth: 125,
  },
  {
    field: "priorEquityToDate",
    cellDataType: "number",
    headerName: "Prior Equity To Date",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.priorEquityToDate),
    width: 125,
    minWidth: 125,
  },
  {
    field: "percentDrawn",
    cellDataType: "number",
    headerName: "% Draw",
    valueFormatter: (params: ValueFormatterParams) => percentFormatter(params, true),
    initialHide: hideColumn(BudgetColumns.percentDrawn),
    width: 75,
    minWidth: 75,
  },
  {
    field: "percentInspectedThisDraw",
    cellDataType: "number",
    headerName: "This Draw Insp",
    headerComponent: HeaderPasteButton,
    headerComponentParams: {
      paste: paste,
      type: "number",
      rounding: 0,
      show: !props.viewOnly,
    },
    valueFormatter: (params: ValueFormatterParams) => percentFormatter(params, params.data.custCostType == null || params.data.costType !== "INSP"),
    editable: (params: EditableCallbackParams) =>
      columnEditable(params) && params.data.costType === "INSP" && !props.drawLocked && !isDrawBasedRetainage(params),
    valueParser: (params: ValueParserParams) => percentParser(params),
    initialHide: hideColumn(BudgetColumns.percentInspectedThisDraw),
    width: 75,
    minWidth: 75,
  },
  {
    field: "projectedOver",
    cellDataType: "number",
    headerName: "Projected Over / (Under)",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.projectedOver),
    width: 125,
    minWidth: 125,
  },
  {
    field: "drawnToDate",
    cellDataType: "number",
    headerName: "Loan Drawn To Date",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.drawnToDate),
    width: 125,
    minWidth: 125,
  },
  {
    field: "netThisDrawEquity",
    cellDataType: "number",
    headerName: "Net This Draw Equity",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.netThisDrawEquity),
    width: 125,
    minWidth: 125,
  },
  {
    field: "equityToDate",
    cellDataType: "number",
    headerName: "Equity Drawn To Date",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.equityToDate),
    width: 125,
    minWidth: 125,
  },
  {
    field: "totalDrawnToDate",
    cellDataType: "number",
    headerName: "Total Drawn To Date",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.totalDrawnToDate),
    width: 125,
    minWidth: 125,
  },
  {
    field: "percentInspectedPriorDraw",
    cellDataType: "number",
    headerName: "Insp Prior Draw",
    valueFormatter: (params: ValueFormatterParams) => percentFormatter(params, true),
    initialHide: hideColumn(BudgetColumns.percentInspectedPriorDraw),
    width: 75,
    minWidth: 125,
  },
  {
    field: "percentInspected",
    cellDataType: "number",
    headerName: "Total Insp",
    valueFormatter: (params: ValueFormatterParams) => percentFormatter(params, true),
    initialHide: hideColumn(BudgetColumns.percentInspected),
    width: 75,
    minWidth: 75,
  },
  {
    field: "variance",
    cellDataType: "number",
    headerName: "Variance",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.variance),
    width: 125,
    minWidth: 125,
  },
  {
    field: "fundsAvailable",
    cellDataType: "number",
    headerName: "Funds Available",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.fundsAvailable),
    width: 125,
    minWidth: 125,
  },
  {
    field: "fundsRequired",
    cellDataType: "number",
    headerName: "Funds Required",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.fundsRequired),
    width: 125,
    minWidth: 125,
  },
  {
    field: "hccPercent",
    cellDataType: "number",
    headerName: "HCC %",
    valueFormatter: (params: ValueFormatterParams) => percentFormatter(params, true),
    initialHide: hideColumn(BudgetColumns.hccPercent),
    width: 75,
    minWidth: 75,
  },
  {
    field: "totalRetainagePriorDraw",
    cellDataType: "number",
    headerName: "Total Retainage Prior Draws",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.totalRetainagePriorDraw),
    width: 125,
    minWidth: 125,
    // hide: !projectInfo.value.hasRetainage,
  },
  {
    field: "totalReleasedPriorDraw",
    cellDataType: "number",
    headerName: "Total Released Prior Draws",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.totalReleasedPriorDraw),
    width: 125,
    minWidth: 125,
    // hide: !projectInfo.value.hasRetainage,
  },
  {
    field: "totalRetainage",
    cellDataType: "number",
    headerName: "Total Retainage",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.totalRetainage),
    width: 125,
    minWidth: 125,
    // hide: !projectInfo.value.hasRetainage,
  },

  {
    field: "totalReleased",
    cellDataType: "number",
    headerName: "Total Released",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.totalReleased),
    width: 125,
    minWidth: 125,
    // hide: !projectInfo.value.hasRetainage,
  },
  {
    field: "retainageBalance",
    cellDataType: "number",
    headerName: "Retainage Balance",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, params.data.custCostType != null),
    initialHide: hideColumn(BudgetColumns.retainageBalance),
    width: 125,
    minWidth: 125,
    // hide: !projectInfo.value.hasRetainage,
  },
  {
    field: "percentBudget",
    cellDataType: "number",
    headerName: "% Amt",
    valueFormatter: percentFormatter,
    editable: (params: EditableCallbackParams) => columnEditable(params) && !props.drawLocked && !isDrawBasedRetainage(params),
    hide: !projectSettings.value.enablePercentageBudget,
    width: 75,
    minWidth: 75,
  },
  {
    field: "cutChecks",
    cellDataType: "boolean",
    headerName: "Cut Checks",
    headerClass: "text-center",
    width: 75,
    minWidth: 75,
    cellRenderer: CutChecksRenderer,
    cellRendererParams: {
      setCutChecks: setCutChecks,
      disabled: props.viewOnly || props.drawLocked,
    },
    initialHide: hideColumn(BudgetColumns.cutChecks) || projectInfo.value.checkingAccount == null,
  },
]);

const cellEditComplete = async (event: CellEditingStoppedEvent) => {
  const colId = event.colDef.field ?? "";
  const row: DrawItem = event.data;
  if (event.newValue !== event.oldValue && colId !== "description" && colId !== "lineRefNumber") {
    const netFunds = projectEntities.value.reduce((accumulator, entity) => accumulator + entity.netFunds, 0);
    drawItems.value = drawItems.value.map((item) =>
      item.drawItemId === row.drawItemId && projectSettings.value.enablePercentageBudget && row.budgetAmount === 0
        ? { ...item, budgetAmount: netFunds * (row.percentBudget / 100), adjustedBudget: row.budgetAmount + row.budgetAdjustment }
        : item
    );

    //Do this to make sure the styling is updated if changed from/to EQUITY
    if (colId === "custCostType") {
      gridApi.value?.refreshCells({
        columns: ["totalThisDraw"],
        force: true,
      });
    }

    await recalculateRows([row], projectSettings.value).then((recalcItems: DrawItem[]) => {
      if (recalcItems.length === 0) return;
      updateRow(event.data.drawItemId, recalcItems[0]);
      nextTick(() => {
        updateTotals(footers, drawItems, gridApi);
      });
    });
    recalculateSourceOfFunds(projectEntities.value, drawItems.value, entityRetainageList.value, projectSettings.value, props.drawNumber === 0).then(
      (recalcEntities: ProjectEntity[]) => (projectEntities.value = recalcEntities)
    );
  }

  updateEntityRetainage(row);

  checkReminders();
};

const updateRow = (id: string, updatedItem: DrawItem): void => {
  drawItems.value = drawItems.value.map((item) => (item.drawItemId === id ? updatedItem : item));
};

const updateEntityRetainage = (item: DrawItem): void => {
  if (entityRetainageList.value == null) return;

  entityRetainageList.value.forEach((er) => {
    const retainage = er.retainageList.find((retainage) => retainage.drawItemId === item.drawItemId);
    if (retainage == null) return;
    retainage.lineRefNumber = item.lineRefNumber;
    retainage.description = item.description;
    retainage.thisDrawEquity = item.totalThisEquity;
    retainage.thisDrawLoan = item.totalThisDraw;
  });
};

const validateEntityRetainage = (): boolean => {
  //TODO: GET THIS WORKING
  return false;
};

const checkReminders = () => {
  if (projectReminders.value.length > 0) {
    let inspectable = 0;
    let percentComplete = 0;
    let totalSiteComplete = 0;

    let totalInspBudget = 0;
    let totalInspDrawn = 0;
    drawItems.value.forEach((item: DrawItem) => {
      if (item.costType === "INSP") {
        inspectable++;
        percentComplete += item.percentInspected;
        totalInspBudget += item.adjustedBudget;
        totalInspDrawn += item.drawnToDate + item.equityToDate;
      }
    });

    if (inspectable > 0) {
      totalSiteComplete = percentComplete / inspectable;
    }

    const totalBudgetDrawn = (totalInspDrawn / totalInspBudget) * 100;

    checkBudgetBasedReminders(
      confirm,
      projectReminders.value,
      drawItems.value,
      totalSiteComplete,
      totalBudgetDrawn,
      projectInfo.value.processor.entityId
    );
  }
};

onMounted(async (): Promise<void> => {
  getCodeTable(CodeTableNumber.CUSTOM_COST_TYPES).then((result: Code[]) => {
    custCostTypes.value = result;
    custCostTypesDisplay.value = custCostTypes.value.flatMap((code: Code) => code.name);
    const custCostTypeCol = colDefs.value.find((def) => def.field === "custCostType");
    if (custCostTypeCol?.cellEditorParams) custCostTypeCol.cellEditorParams.values = custCostTypes.value.flatMap((code) => code.name);
    gridApi.value?.setGridOption("columnDefs", colDefs.value);
  });

  getBudgets();
  getSpecialLineItems();
  updateTotals(footers, drawItems, gridApi);
  showColumns(projectInfo.value, projectSettings.value.budgetColumns, gridApi);
});
</script>
